import React, { useEffect, useRef, useState } from 'react';
import { Copy, ThumbsUp, ThumbsDown } from 'lucide-react';
import type { MessageReaction } from '../../types';
import type { WidgetStrings } from '../../utils/i18n';
import { DEFAULT_WIDGET_STRINGS } from '../../utils/i18n';
import { FEEDBACK_COMMENT_MAX_LENGTH } from '../../hooks/useMessageFeedback';

interface MessageActionsProps {
  /** Controlled: the reaction currently stored on the message. */
  reaction: MessageReaction | undefined;
  onCopy: () => void;
  /** Receives the next reaction; clicking the active thumb again yields `null` (clear). */
  onReaction: (reaction: MessageReaction) => void;
  strings?: WidgetStrings;
  /** Force the actions visible (hover/focus state tracked by the bubble); CSS also reveals on :hover / :focus-within */
  visible?: boolean;
  /** Lets the bubble return focus to the thumbs-down button after the comment input closes. */
  thumbsDownRef?: React.Ref<HTMLButtonElement>;
}

const MessageActions = React.memo(function MessageActions({
  reaction,
  onCopy,
  onReaction,
  strings = DEFAULT_WIDGET_STRINGS,
  visible = false,
  thumbsDownRef,
}: MessageActionsProps) {
  const current = reaction ?? null;
  const toggle = (type: 'up' | 'down') => onReaction(current === type ? null : type);
  return (
    <div
      className={`rw-message-actions${visible ? ' rw-message-actions-visible' : ''} absolute -top-2 -right-2 flex gap-1 bg-white border rounded-lg shadow-sm p-1`}
    >
      <button
        onClick={onCopy}
        type="button"
        className="p-1 hover:bg-gray-100 rounded focus:outline-2 focus:outline-blue-500"
        title={strings.copyMessage}
        aria-label={strings.copyMessage}
      >
        <Copy className="w-3 h-3 text-gray-600" aria-hidden="true" />
      </button>
      <button
        onClick={() => toggle('up')}
        type="button"
        className={`p-1 hover:bg-gray-100 rounded focus:outline-2 focus:outline-blue-500 ${current === 'up' ? 'bg-green-50' : ''}`}
        title={strings.goodResponse}
        aria-label={strings.thumbsUp}
        aria-pressed={current === 'up'}
      >
        {/* Pressed is shown by a filled icon too, not by colour alone (WCAG 1.4.1). */}
        <ThumbsUp
          className={`w-3 h-3 ${current === 'up' ? 'text-green-600' : 'text-gray-600'}`}
          fill={current === 'up' ? 'currentColor' : 'none'}
          aria-hidden="true"
        />
      </button>
      <button
        ref={thumbsDownRef}
        onClick={() => toggle('down')}
        type="button"
        className={`p-1 hover:bg-gray-100 rounded focus:outline-2 focus:outline-blue-500 ${current === 'down' ? 'bg-red-50' : ''}`}
        title={strings.notHelpful}
        aria-label={strings.thumbsDown}
        aria-pressed={current === 'down'}
      >
        <ThumbsDown
          className={`w-3 h-3 ${current === 'down' ? 'text-red-600' : 'text-gray-600'}`}
          fill={current === 'down' ? 'currentColor' : 'none'}
          aria-hidden="true"
        />
      </button>
    </div>
  );
});

export default MessageActions;

interface FeedbackCommentInputProps {
  /** Called with the trimmed comment on Enter (never empty). */
  onSubmit: (comment: string) => void;
  /** Called on Escape, or on Enter with nothing typed. */
  onDismiss: () => void;
  strings?: WidgetStrings;
}

const COMMENT_INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  minWidth: 0,
  fontSize: '12px',
  lineHeight: '18px',
  padding: '4px 8px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  background: '#fff',
  color: '#111827',
};

/**
 * One-line optional comment shown after a thumbs-down. Focus moves in on mount; the
 * parent owns where focus goes afterwards (back to the thumb) via `onDismiss`/`onSubmit`.
 */
export function FeedbackCommentInput({
  onSubmit,
  onDismiss,
  strings = DEFAULT_WIDGET_STRINGS,
}: FeedbackCommentInputProps) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      // Keep the widget-level Escape handler from closing the whole chat.
      event.preventDefault();
      event.stopPropagation();
      onDismiss();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const trimmed = value.trim();
      if (trimmed) onSubmit(trimmed.slice(0, FEEDBACK_COMMENT_MAX_LENGTH));
      else onDismiss();
    }
  };

  return (
    <div className="rw-feedback-comment mt-2">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={event => setValue(event.target.value.slice(0, FEEDBACK_COMMENT_MAX_LENGTH))}
        onKeyDown={handleKeyDown}
        maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
        placeholder={strings.feedbackCommentPlaceholder}
        aria-label={strings.feedbackCommentPlaceholder}
        autoComplete="off"
        enterKeyHint="send"
        className="rw-feedback-comment-input"
        style={COMMENT_INPUT_STYLE}
      />
    </div>
  );
}
